import { baseApi } from '../baseApi';

export interface ChatGroup {
  id: string;
  classId: string;
  name: string;
  yearId?: string | null;
  educationYear: string;
  description?: string | null;
  status: 'active' | 'inactive';
  academicYear?: {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    isActive: boolean;
  };
  createdAt: string;
  updatedAt: string;
  stats?: {
    totalMembers: number;
    activeMembers: number;
    inactiveMembers: number;
    removedMembers: number;
    totalMessages: number;
  };
  lastMessage?: {
    id: string;
    senderName: string;
    senderType: 'ADMIN' | 'USER';
    messageType: string;
    messageText: string;
    isDeleted: boolean;
    createdAt: string;
  } | null;
}

export interface ChatMember {
  id: string;
  groupId: string;
  studentId: string;
  status: 'active' | 'inactive' | 'removed';
  joinedAt: string;
  leftAt?: string | null;
  removedAt?: string | null;
  isRemovedByAdmin: boolean;
  student?: {
    id: string;
    name: string;
    email: string;
    mobile?: string;
    collegeName?: string;
    state?: string;
    status: 'active' | 'inactive';
  };
}

export interface ChatMessage {
  id: string;
  groupId: string;
  senderId: string;
  senderType: 'ADMIN' | 'USER';
  senderName: string;
  messageType: 'TEXT' | 'IMAGE' | 'VIDEO' | 'PDF' | 'AUDIO' | 'FILE';
  messageText?: string | null;
  fileUrl?: string | null;
  fileName?: string | null;
  fileSize?: number | null;
  mimeType?: string | null;
  replyToMessageId?: string | null;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: string | null;
  createdAt: string;
  updatedAt: string;
  replyToMessage?: {
    id: string;
    senderName: string;
    senderType: string;
    messageText: string;
    messageType: string;
    isDeleted: boolean;
  };
}

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const chatApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getChatGroups: builder.query<{ success: boolean; data: ChatGroup[]; pagination: Pagination }, {
      search?: string;
      status?: string;
      yearId?: string;
      page?: number;
      limit?: number;
    } | void>({
      query: (params) => ({
        url: '/api/admin/chat/groups',
        params: params || {},
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'ChatGroup' as const, id })),
              { type: 'ChatGroup', id: 'LIST' },
            ]
          : [{ type: 'ChatGroup', id: 'LIST' }],
    }),

    getChatGroup: builder.query<{ success: boolean; data: ChatGroup }, string>({
      query: (id) => `/api/admin/chat/groups/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'ChatGroup', id }],
    }),

    createChatGroup: builder.mutation<{ success: boolean; message: string; data: ChatGroup }, Partial<ChatGroup>>({
      query: (body) => ({
        url: '/api/admin/chat/groups',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ChatGroup', id: 'LIST' }],
    }),

    updateChatGroup: builder.mutation<{ success: boolean; message: string; data: ChatGroup }, { id: string; [key: string]: any }>({
      query: ({ id, ...body }) => ({
        url: `/api/admin/chat/groups/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ChatGroup', id },
        { type: 'ChatGroup', id: 'LIST' },
      ],
    }),

    deleteChatGroup: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `/api/admin/chat/groups/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ChatGroup', id: 'LIST' }],
    }),

    syncAllChatGroups: builder.mutation<{ success: boolean; message: string; data: any }, void>({
      query: () => ({
        url: '/api/admin/chat/sync-all',
        method: 'POST',
      }),
      invalidatesTags: [
        { type: 'ChatGroup', id: 'LIST' },
        { type: 'ChatMember', id: 'LIST' },
      ],
    }),

    getGroupMembers: builder.query<{ success: boolean; data: ChatMember[]; pagination: Pagination }, {
      groupId: string;
      search?: string;
      status?: string;
      page?: number;
      limit?: number;
    }>({
      query: ({ groupId, ...params }) => ({
        url: `/api/admin/chat/groups/${groupId}/members`,
        params,
      }),
      providesTags: (_result, _error, { groupId }) => [
        { type: 'ChatMember', id: groupId },
        { type: 'ChatMember', id: 'LIST' },
      ],
    }),

    updateMemberStatus: builder.mutation<{ success: boolean; message: string; data: ChatMember }, {
      groupId: string;
      memberId: string;
      status: 'active' | 'inactive';
    }>({
      query: ({ groupId, memberId, status }) => ({
        url: `/api/admin/chat/groups/${groupId}/members/${memberId}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (_result, _error, { groupId }) => [
        { type: 'ChatMember', id: groupId },
        { type: 'ChatGroup', id: groupId },
      ],
    }),

    addMembersToGroup: builder.mutation<{ success: boolean; message: string; addedCount: number }, {
      groupId: string;
      studentIds: string[];
    }>({
      query: ({ groupId, studentIds }) => ({
        url: `/api/admin/chat/groups/${groupId}/members`,
        method: 'POST',
        body: { studentIds },
      }),
      invalidatesTags: (_result, _error, { groupId }) => [
        { type: 'ChatMember', id: groupId },
        { type: 'ChatMember', id: 'LIST' },
        { type: 'ChatGroup', id: groupId },
        { type: 'ChatGroup', id: 'LIST' },
      ],
    }),

    removeMember: builder.mutation<{ success: boolean; message: string }, {
      groupId: string;
      memberId: string;
    }>({
      query: ({ groupId, memberId }) => ({
        url: `/api/admin/chat/groups/${groupId}/members/${memberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { groupId }) => [
        { type: 'ChatMember', id: groupId },
        { type: 'ChatGroup', id: groupId },
      ],
    }),

    getGroupMessages: builder.query<{ success: boolean; data: ChatMessage[] }, {
      groupId: string;
      search?: string;
      before?: string;
      limit?: number;
    }>({
      query: ({ groupId, ...params }) => ({
        url: `/api/admin/chat/groups/${groupId}/messages`,
        params,
      }),
      providesTags: (_result, _error, { groupId }) => [
        { type: 'ChatMessage', id: groupId },
      ],
    }),

    sendMessage: builder.mutation<{ success: boolean; data: ChatMessage }, {
      groupId: string;
      messageText?: string;
      messageType?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'PDF' | 'AUDIO' | 'FILE';
      fileUrl?: string;
      fileName?: string;
      fileSize?: number;
      mimeType?: string;
      replyToMessageId?: string;
    }>({
      query: ({ groupId, ...body }) => ({
        url: `/api/admin/chat/groups/${groupId}/messages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { groupId }) => [
        { type: 'ChatMessage', id: groupId },
        { type: 'ChatGroup', id: groupId },
        { type: 'ChatGroup', id: 'LIST' },
      ],
    }),

    editMessage: builder.mutation<{ success: boolean; message: string; data: ChatMessage }, {
      messageId: string;
      groupId: string;
      messageText: string;
    }>({
      query: ({ messageId, messageText }) => ({
        url: `/api/admin/chat/messages/${messageId}`,
        method: 'PATCH',
        body: { messageText },
      }),
      invalidatesTags: (_result, _error, { groupId }) => [
        { type: 'ChatMessage', id: groupId },
        { type: 'ChatGroup', id: groupId },
      ],
    }),

    deleteMessage: builder.mutation<{ success: boolean; message: string; messageId: string }, {
      messageId: string;
      groupId: string;
    }>({
      query: ({ messageId }) => ({
        url: `/api/admin/chat/messages/${messageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { groupId }) => [
        { type: 'ChatMessage', id: groupId },
        { type: 'ChatGroup', id: groupId },
      ],
    }),

    deleteAllMemberMessages: builder.mutation<{ success: boolean; message: string }, {
      groupId: string;
      memberId: string;
      studentId?: string;
    }>({
      query: ({ groupId, memberId, studentId }) => ({
        url: `/api/admin/chat/groups/${groupId}/members/${memberId}/messages`,
        method: 'DELETE',
        body: { studentId },
      }),
      invalidatesTags: (_result, _error, { groupId }) => [
        { type: 'ChatMessage', id: groupId },
        { type: 'ChatGroup', id: groupId },
      ],
    }),
  }),
});

export const {
  useGetChatGroupsQuery,
  useGetChatGroupQuery,
  useCreateChatGroupMutation,
  useUpdateChatGroupMutation,
  useDeleteChatGroupMutation,
  useSyncAllChatGroupsMutation,
  useGetGroupMembersQuery,
  useAddMembersToGroupMutation,
  useUpdateMemberStatusMutation,
  useRemoveMemberMutation,
  useGetGroupMessagesQuery,
  useSendMessageMutation,
  useEditMessageMutation,
  useDeleteMessageMutation,
  useDeleteAllMemberMessagesMutation,
} = chatApi;
